import type { FulfillmentOrderLine, FulfillmentResourceMentionCandidate } from "../types";
import type { EntryParseBaseInput, EntryParseResult } from "./confirmationEntryTypes";
import { roles, factors, unit, counts, EntryParseError, parseEntryDates, parseEntrySegments } from "./confirmationEntryPrimitives";
import { calculateFulfillmentLineAmount, confirmationTravelerRoleLabel, validateFulfillmentLineDraft } from "./fulfillmentOrderEditorService";
import { confirmationDecimalInput, formatConfirmationAmount } from "./confirmationDecimalDisplay";

/** 门票的两个 SKU 选择入口共用角色人数和该 SKU 的人民币参考价，缺失值不得当零。 */
export function buildTicketSkuDefaults(candidate: FulfillmentResourceMentionCandidate, roleCounts: EntryParseBaseInput["roleCounts"] = {}) {
  const role = confirmationTravelerRoleLabel(candidate.travelerRole).replace(/^教师$/, "老师");
  const count = roleCounts[role];
  const quantity = count != null && Number.isFinite(count) && count >= 0 ? String(count) : "待填";
  const rawPrice = candidate.referenceUnitPrice?.trim();
  const price = candidate.referencePriceCurrency === "CNY" && rawPrice && /^\d{1,16}(?:\.\d{1,2})?$/.test(rawPrice) ? confirmationDecimalInput(rawPrice) : null;
  return {
    role,
    formula: role ? `${role}${quantity}×1×${price ?? "待填"}` : `1场×${price ?? "待填"}`,
    priceLabel: price === null ? "价格待填" : formatConfirmationAmount(price),
    priceSourceLabel: price === null ? "暂无可用的 SKU 参考价" : `${candidate.referencePriceType === "historical_sample" ? "历史参考价" : "SKU 参考价"}${candidate.referencePriceDate ? ` · ${candidate.referencePriceDate}` : ""}，请核对`,
  };
}

/** 没有明确到访日期时不虚构日次；原行已有日期继续用于编辑基线。 */
function datePatch(input: EntryParseBaseInput, range = input.range): Partial<FulfillmentOrderLine> {
  if (!range.trim()) return { dayExpression: "", dayNumbers: [], serviceDates: [], serviceDateFrom: null, serviceDateTo: null, dateMappingStatus: "mapped" };
  return parseEntryDates(input.routeDays, range, "服务", "control");
}

/** 本分类服务主体资格与结算主体分别保留。 */
function providerPatch(input: EntryParseBaseInput): Partial<FulfillmentOrderLine> {
  const supplier = input.provider && "id" in input.provider ? input.provider : null;
  if (supplier && "snapshotResourceType" in supplier) {
    if (supplier.snapshotResourceType !== "考察点") throw new EntryParseError(`服务主体“${supplier.name}”属于${supplier.snapshotResourceType}，不能用于考察点；请重新选择本类服务主体`, "provider");
  } else if (supplier) {
    if (!supplier.canBeFulfillmentSubject) throw new EntryParseError(`服务主体“${supplier.name}”不具备当前履约资格，请重新选择可履约的主体`, "provider");
  }
  return {
    fulfillmentSupplierId: supplier?.id ?? null,
    fulfillmentSupplierName: supplier?.name ?? null,
    settlementSupplierId: input.settlement?.id ?? null,
    settlementSupplierName: input.settlement?.name ?? null,
  };
}
/** 本分类明确资源类型和计价单位后校验完整行。 */
function basePatch(input: EntryParseBaseInput, name: string, quantity: string, count: string, price: string, quantityUnit: string, extra: Partial<FulfillmentOrderLine> = {}): Partial<FulfillmentOrderLine> {
  const amount = calculateFulfillmentLineAmount(quantity, count, price);
  const patch: Partial<FulfillmentOrderLine> = {
    resourceType: "考察点", itemNameSnapshot: name, itemSpecSnapshot: null,
    quantity, billingCount: count, unitPriceAmount: price, amount, unit: quantityUnit,
    pricingUnitType: "unit",
    reviewStatus: "confirmed", sourceType: "manual", status: "active", ...datePatch(input), ...providerPatch(input), ...extra,
  };
  const errors = validateFulfillmentLineDraft({ ...patch, id: input.allowZeroQuantity ? "existing-preview" : "local-preview" } as FulfillmentOrderLine);
  if (errors.length) throw new Error(errors.join("；"));
  return patch;
}
/** 解析当前分类支持的角色、人数及显式次数。 */
function personFormula(input: EntryParseBaseInput, text: string) {
  const roleMatch = text.match(new RegExp(`^(${roles})\\s*(.*)$`));
  const role = roleMatch?.[1].replace("教师", "老师") ?? "";
  const parts = factors(roleMatch ? roleMatch[2] : text, undefined, input.allowZeroQuantity);
  const price = parts.at(-1)!.value;
  if (!role) {
    if (parts.length !== 2) throw new Error("按场费用请填写场次×单价；按人费用请明确角色和人数");
    unit(parts[0], ["场", "次"]);
    return { role: null, quantity: parts[0].value, count: "1", price, unit: "场" };
  }
  if (!role) throw new Error("请明确学生、老师、导游或司机角色和实际人数");
  if (parts.length === 1) return { role, quantity: counts(input, role), count: "1", price, unit: "人" };
  unit(parts[0], ["人"]);
  if (parts.length === 3) unit(parts[1], ["次"]);
  return { role, quantity: parts[0].value, count: parts.length === 3 ? parts[1].value : "1", price, unit: "人" };
}
/** 解析本类单段原文，保留本类资源和计费约束。 */
function parseSegment(input: EntryParseBaseInput, text: string): Partial<FulfillmentOrderLine>[] {
  const raw = text.trim();
  if (!input.point?.spuId || !input.point.spuName) throw new EntryParseError("尚未选择考察点，请在上方行程考察点中选择本次到访的真实考察点", "resource");
  const content = raw.replace(/^[@＠#＃]/, "");
  const nameKey = (name: string) => name.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();
  const knownNames = new Set(input.candidates.filter((item) => item.resourceType === "考察点" && item.spuId === input.point!.spuId && item.level === "sku" && item.skuName).map((item) => nameKey(item.skuName!)));
  const knownBoundary = [...content.matchAll(/\s+/g)].reverse().find((boundary) => knownNames.has(nameKey(content.slice(0, boundary.index))));
  const match = knownBoundary ? [content, content.slice(0, knownBoundary.index), content.slice(knownBoundary.index + knownBoundary[0].length)] : content.match(new RegExp(`^(.+?)\\s+((?:${roles})?\\s*[\\d０-９].*)$`));
  if (!match) throw new Error("门票请填写 @规格 角色人数×次数×单价，按场填 @讲解 2场×800；旧#写法继续兼容");
  const p = personFormula(input, match[2]);
  const inputNameKey = nameKey(match[1]);
  const candidates = input.candidates.filter((item) => item.resourceType === "考察点" && item.spuId === input.point!.spuId && item.skuName != null && nameKey(item.skuName) === inputNameKey);
  if (/^[@＠]/.test(raw) && p.role && !candidates.some((item) => item.level === "sku")) throw new EntryParseError(`当前考察点没有已选择的“${match[1]}”规格，请在文本中输入@搜索并选择该点真实SKU`, "resource");
  const roleAliases: Record<string, string> = { 学生: "student", 老师: "teacher", 导游: "guide", 司机: "driver", 成人: "adult", 儿童: "child" };
  if (p.role && candidates.length && !candidates.some((candidate) => !candidate.travelerRole || candidate.travelerRole === (roleAliases[p.role!] || p.role))) throw new Error("旅客角色与所选考察点票种不一致，请选择对应角色票种");
  return [basePatch(input, input.point.spuName, p.quantity, p.count, p.price, p.unit, {
    itemSpecSnapshot: match[1], travelerRole: p.role,
    resourceSelection: { kind: "point", pointSpuId: input.point.spuId, skuName: match[1] }
  })];
}

/** 独立解析考察点文本，保留本类日期、计费和资源规则。 */
export function parseTicketEntry(input: EntryParseBaseInput): EntryParseResult {
  return parseEntrySegments(input.text, text => parseSegment(input, text), patch => [...(!patch.settlementSupplierId ? ["结算主体待设置"] : []), ...(!patch.fulfillmentSupplierId ? ["服务主体待核对"] : [])]);
}
